import TYPES from './types';

import { IDashboardError } from './reducers';
import {
  dashboardNewPasswordRepeatSel,
  dashboardCurrentPasswordSel,
  dashboardNewPasswordSel
} from './selectors';
import PowerUserModel from 'src/model/powerUser.model';
import PowerUserService from 'src/services/poweruser/powerUserService';
import UserService from 'src/services/user/userService';
import { INewPasswordValidation } from 'src/model/modules/dashboardview.model';
import EcheckPermissionService from 'src/services/echeckPermission/echeckPermissionService';
import EcheckPermissionModel from 'src/model/echeckPermission.model';

const setIsPending = (isPending: boolean) => {
  return {
    type: TYPES.SET_IS_PENDING,
    payload: isPending ? isPending : false
  };
};

const setError = (error?: IDashboardError) => {
  return {
    type: TYPES.SET_ERROR,
    payload: error
      ? error
      : {
        hasFailed: false,
        message: ''
      }
  };
};

const setMenuOption = (option: 'switch' | 'manage' | 'password') => {
  return {
    type: TYPES.SET_MENU_OPTION,
    payload: option
  };
};

const searchPowerUsersResults = (results: PowerUserModel[]) => {
  return { type: TYPES.POWER_USER_SEARCH_RESULTS, payload: results };
};

const setHasSucceed = (success: boolean) => {
  return { type: TYPES.DASHBOARD_VIEW_SUCCEED, payload: success };
};

const searchPowerUsers = (text: string) => {
  return (dispatch: any) => {
    dispatch(setError(null));
    dispatch(setIsPending(true));
    PowerUserService.searchPowerUsers(text)
      .then((response: any) => {
        dispatch(searchPowerUsersResults(response.data));
        dispatch(setIsPending(false));
      })
      .catch((err: any) => {
        dispatch(setError({ hasFailed: true, message: err }));
        dispatch(setIsPending(false));
      });
  };
};

const addPowerUser = (
  email: string,
  fullName: string,
  handlerId: number,
  mobileNumber: string,
  password: string,
  username: string
) => {
  return (dispatch: any) => {
    dispatch(setError(null));
    dispatch(setHasSucceed(false));
    PowerUserService.addPowerUser(
      email,
      fullName,
      handlerId,
      mobileNumber,
      password,
      username
    )
      .then((response: any) => {
        dispatch(searchPowerUsers(''));
        dispatch(setHasSucceed(true));
      })
      .catch((err: any) => {
        dispatch(
          setError({ hasFailed: true, message: err.response.data.message })
        );
      });
  };
};

const deletePowerUser = (id: number) => {
  return (dispatch: any) => {
    dispatch(setError(null));
    dispatch(setHasSucceed(false));
    PowerUserService.deletePowerUser(id)
      .then((response: any) => {
        dispatch(searchPowerUsers(''));
        dispatch(setHasSucceed(true));
      })
      .catch((err: any) => {
        dispatch(setError({ hasFailed: true, message: err }));
      });
  };
};

const setCurrentPassword = (value: string) => {
  return {
    type: TYPES.SET_DASHBOARD_CURRENT_PASSWORD,
    payload: value
  };
};

// const setUnlockUser = (value: string) => {
//   return {
//     type: TYPES.SET_UNLOCK_USER,
//     payload: value
//   };
// };

const rawSetNewPassword = (value: string) => {
  return {
    type: TYPES.SET_DASHBOARD_NEW_PASSWORD,
    payload: value
  };
};

const rawSetNewPasswordRepeat = (value: string) => {
  return {
    type: TYPES.SET_DASHBOARD_NEW_PASSWORD_REPEAT,
    payload: value
  };
};

const validatePasswords = (pass1: string, pass2: string) => {
  const validation = {
    valid: false,
    dirty: false,
    message: ''
  };
  if (pass1.length && pass2.length) {
    validation.valid = true;
    validation.dirty = true;
    if (pass1 !== pass2) {
      validation.valid = false;
      validation.message = "Passwords don't match";
    } else if (pass1.length < 8 || pass2.length < 8) {
      validation.valid = false;
      validation.message = 'Minimum 8 characters';
    }
  } else {
    validation.valid = false;
    validation.dirty = false;
    validation.message = '';
  }

  return validation;
};

const setNewPassword = (value: string) => {
  return (dispatch: any, getState: any) => {
    // Validate new password and repeat
    const repeat = dashboardNewPasswordRepeatSel(getState());
    const validation = validatePasswords(value, repeat);
    dispatch(setNewPasswordValidation(validation));
    dispatch(rawSetNewPassword(value));
  };
};

const setNewPasswordRepeat = (value: string) => {
  return (dispatch: any, getState: any) => {
    // Validate new password and repeat
    const newpass = dashboardNewPasswordSel(getState());
    const validation = validatePasswords(value, newpass);
    dispatch(setNewPasswordValidation(validation));
    dispatch(rawSetNewPasswordRepeat(value));
  };
};

const setNewPasswordValidation = (validation: INewPasswordValidation) => {
  return {
    type: TYPES.SET_DASHBOARD_NEW_PASSWORD_VALIDATION,
    payload: validation
  };
};

const changePassword = (cb?: (result: boolean) => void) => {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const currentPassword = dashboardCurrentPasswordSel(state);
    const newPassword = dashboardNewPasswordSel(state);
    dispatch(setError());
    dispatch(setIsPending(true));
    try {
      const result = await UserService.changePassword(
        currentPassword,
        newPassword
      );
      if (cb) {
        cb(result);
      }
      dispatch(setMenuOption('switch'));
      dispatch(setIsPending(false));
    } catch (err) {
      console.error(err);
      dispatch(
        setError({ hasFailed: true, message: "Couldn't change password" })
      );
      dispatch(setIsPending(false));
    }
  };
};

const fetchEcheckDisabledUsersResults = (results: EcheckPermissionModel[]) => {
  return { type: TYPES.ECHECK_DISABLED_USER_SEARCH_RESULTS, payload: results };
};

const fetchEcheckDisabledUsers = (): any => {
  return (dispatch: any) => {
    dispatch(setError(null));
    dispatch(setIsPending(true));
    EcheckPermissionService.fetchEcheckDisabledUsers()
      .then((response: any) => {
        dispatch(fetchEcheckDisabledUsersResults(response.data));
        dispatch(setIsPending(false));
      })
      .catch((err: any) => {
        dispatch(setError({ hasFailed: true, message: err }));
        dispatch(setIsPending(false));
      });
  };
};

const EcheckUserStatusToggle = (
  usernameArray: string[],
  status: boolean
) => {
  return (dispatch: any) => {
    dispatch(setError(null));
    dispatch(setHasSucceed(false));
    EcheckPermissionService.changeUserEcheckStatus(
      usernameArray, 
      status
    )
      .then((response: any) => {
        dispatch(setHasSucceed(true));
      })
      .catch((err: any) => {
        dispatch(
          setError({ hasFailed: true, message: err.response })
        );
      });
  };
};

export default {
  setError,
  setIsPending,
  setMenuOption,
  setHasSucceed,
  searchPowerUsers,
  addPowerUser,
  deletePowerUser,
  setCurrentPassword,
  setNewPassword,
  setNewPasswordRepeat,
  changePassword,
  setNewPasswordValidation,
  fetchEcheckDisabledUsers,
  EcheckUserStatusToggle
};
